import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Loader2 } from "lucide-react";
import { Btn } from "@/components/kit";
import { API } from "@/lib/api";
import { WAITLIST_TOPICS } from "@/config/platform";

export function WaitlistForm({ defaults = [], testId = "waitlist" }) {
  const [email, setEmail] = useState("");
  const [topics, setTopics] = useState(defaults.length ? defaults : WAITLIST_TOPICS.map((t) => t.id));
  const [hp, setHp] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const toggle = (id) => setTopics((ts) => (ts.includes(id) ? ts.filter((t) => t !== id) : [...ts, id]));
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && topics.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), interests: topics, website: hp }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Inscription impossible");
      setState("done");
    } catch (err) {
      setState("idle");
      setError(err.message);
    }
  };

  if (state === "done")
    return (
      <div className="rounded-[20px] border border-gold/50 bg-white p-6" data-testid={`${testId}-success`}>
        <p className="flex items-center gap-2 font-display text-xl font-semibold text-bx"><Check className="h-5 w-5 text-gold" />C'est noté !</p>
        <p className="mt-2 text-soft">Tu recevras un email de confirmation, puis un message dès l'ouverture de ce qui t'intéresse.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="rounded-[20px] border border-line bg-white p-6 sm:p-7" data-testid={testId}>
      <p className="flex items-center gap-2 font-display text-xl font-semibold text-bx"><Bell className="h-5 w-5 text-gold" strokeWidth={1.6} />Être prévenue de l'ouverture</p>
      <p className="mt-2 text-sm text-soft">Choisis ce qui t'intéresse, on t'écrit une seule fois à l'ouverture.</p>
      <fieldset className="mt-5 flex flex-wrap gap-2">
        <legend className="sr-only">Sujets</legend>
        {WAITLIST_TOPICS.map((t) => {
          const on = topics.includes(t.id);
          return (
            <button key={t.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(t.id)} data-testid={`${testId}-topic-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${on ? "border-bx bg-bx text-white" : "border-line bg-ivory text-ink hover:border-bx/40"}`}>
              {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}{t.label}
            </button>
          );
        })}
      </fieldset>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor={`${testId}-email`} className="sr-only">Ton email</label>
        <input id={`${testId}-email`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ton@email.com" autoComplete="email"
          className="min-w-0 flex-1 rounded-full border border-line bg-white px-5 py-3 text-base outline-none focus:border-bx focus:ring-2 focus:ring-bx/20" data-testid={`${testId}-email`} />
        <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" name="website" />
        <Btn type="submit" disabled={!valid || state === "sending"} data-testid={`${testId}-submit`}>
          {state === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}Me prévenir
        </Btn>
      </div>
      {error && <p className="mt-3 text-sm text-red-700" data-testid={`${testId}-error`}>{error}</p>}
      <p className="mt-4 text-xs text-soft">
        Ton email sert uniquement à te prévenir. Désinscription à tout moment en répondant à nos emails. <Link to="/confidentialite" className="underline underline-offset-2">Confidentialité</Link>
      </p>
    </form>
  );
}
